import React, { useState, useEffect, useRef } from 'react';

const PWAInstallButton = ({ className = '', children }) => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);
  const deferredPromptRef = useRef(null);
  const isProduction = process.env.NODE_ENV === 'production';

  useEffect(() => {
    if (!isProduction) return;

    // Check if app is already installed
    const checkIfInstalled = () => {
      const isStandalone = 
        window.matchMedia('(display-mode: standalone)').matches ||
        window.matchMedia('(display-mode: window-controls-overlay)').matches ||
        window.navigator.standalone === true ||
        document.referrer.includes('android-app://');

      if (isStandalone) {
        setIsInstalled(true);
        return;
      }

      // Check if it was recently installed
      const installed = localStorage.getItem('pwa-installed');
      if (installed === 'true') {
        setIsInstalled(true);
      }
    };

    // Detect iOS Safari (doesn't support beforeinstallprompt)
    const detectIOS = () => {
      const userAgent = window.navigator.userAgent;
      const isIOSDevice = /iPad|iPhone|iPod/.test(userAgent);
      const isSafari = /Safari/.test(userAgent) && !/CriOS|FxiOS/.test(userAgent);
      
      if (isIOSDevice && isSafari && !window.navigator.standalone) {
        setIsIOS(true);
        // Check if we already showed the hint and user dismissed it
        const dismissed = localStorage.getItem('ios-install-hint-dismissed');
        if (dismissed !== 'true') {
          setIsInstallable(true);
        }
      }
    };

    // Listen for beforeinstallprompt event (Android Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setDeferredPrompt(e);
      setIsInstallable(true);
      console.log('✅ PWA: beforeinstallprompt event captured');
    };

    // Listen for appinstalled event
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      deferredPromptRef.current = null;
      localStorage.setItem('pwa-installed', 'true');
      console.log('✅ PWA berhasil diinstall!');
    };

    // Listen for display mode changes (app installed/uninstalled)
    const mediaQuery = window.matchMedia('(display-mode: standalone)');
    const handleMediaChange = (e) => {
      if (!e.matches) {
        // App might have been uninstalled
        setIsInstalled(false);
        localStorage.removeItem('pwa-installed');
      }
    };

    checkIfInstalled();
    detectIOS();

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleMediaChange);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleMediaChange);
      }
    };
  }, [isProduction]);

  const handleInstallClick = async () => {
    // iOS Safari - show install instructions
    if (isIOS) {
      setShowIOSHint(true);
      return;
    }

    // Android/Chrome - use deferred prompt
    const prompt = deferredPrompt || deferredPromptRef.current;
    if (!prompt) {
      console.warn('PWA: No deferred prompt available');
      return;
    }

    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('✅ User menerima install prompt');
      } else {
        console.log('User menolak install prompt');
      }
      
      setDeferredPrompt(null);
      deferredPromptRef.current = null;
      setIsInstallable(false);
    } catch (error) {
      console.error('Error saat install PWA:', error);
    }
  };

  const dismissIOSHint = () => {
    setShowIOSHint(false);
    setIsInstallable(false);
    localStorage.setItem('ios-install-hint-dismissed', 'true');
  };

  // Don't show button if not production, already installed, or not installable
  if (!isProduction || isInstalled || !isInstallable) {
    return null;
  }

  return (
    <>
      <button
        onClick={handleInstallClick}
        className={className || `
          inline-flex items-center px-3 py-1.5 
          bg-primary text-white rounded-lg text-sm
          hover:bg-primary-600 transition-colors duration-200
          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        `}
        title="Install aplikasi ke perangkat Anda"
      >
        <svg 
          className="w-4 h-4 mr-1.5" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" 
          />
        </svg>
        {children || 'Install'}
      </button>

      {/* iOS Install Instructions Modal */}
      {showIOSHint && (
        <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4 bg-black/50" onClick={dismissIOSHint}>
          <div 
            className="bg-white rounded-2xl shadow-2xl p-6 max-w-sm w-full" 
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 mb-3">
              📲 Install di iPhone/iPad
            </h3>
            <div className="space-y-3 text-sm text-gray-600">
              <p className="flex items-start gap-2">
                <span className="font-medium">1.</span>
                <span>Tap tombol <strong>Share</strong> (ikon kotak dengan panah ke atas) di bar bawah Safari</span>
              </p>
              <p className="flex items-start gap-2">
                <span className="font-medium">2.</span>
                <span>Scroll ke bawah dan pilih <strong>"Add to Home Screen"</strong></span>
              </p>
              <p className="flex items-start gap-2">
                <span className="font-medium">3.</span>
                <span>Tap <strong>"Add"</strong> di pojok kanan atas</span>
              </p>
            </div>
            <button
              onClick={dismissIOSHint}
              className="mt-4 w-full py-2.5 bg-primary text-white rounded-xl font-medium hover:bg-primary-600 transition-colors"
            >
              Mengerti
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default PWAInstallButton;